import { useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui';
import { ChatMessage, ChatInterfaceProps } from '../../../types';

const ChatInterface: React.FC<ChatInterfaceProps> = ({ className = '' }) => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Hello! I\'m here to help you understand and implement the pedagogical suggestions for your class. Feel free to ask me about any of the recommendations or student clustering insights.',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: newMessage,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);

    // Simulate AI response
    setTimeout(() => {
      const responses = [
        "That's a great question! Based on the clustering analysis, I'd recommend focusing on differentiated instruction strategies that cater to each group's strengths.",
        "The research supports this approach. Studies show that when teaching methods align with student preferences, engagement increases by up to 60%.",
        "For implementing this suggestion, start small with one strategy per week and gather student feedback to refine your approach.",
        "This aligns perfectly with the pedagogical framework we're using. Would you like me to provide specific implementation steps?",
        "Based on your class composition, this strategy would be particularly effective for your Collaborative Communicators group."
      ];

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: responses[Math.floor(Math.random() * responses.length)],
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    }, 1000);

    setNewMessage('');
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg h-96 flex flex-col ${className}`}>
      <div className="p-4 border-b border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900">Ask About Your Suggestions</h4>
        <p className="text-sm text-gray-600">Get clarification and implementation guidance</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {chatMessages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.type === 'user'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className="text-sm">{message.content}</p>
              <p className="text-xs mt-1 opacity-70">
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Ask about implementation, research, or specific strategies..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <Button
            onClick={handleSendMessage}
            size="sm"
            leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface; 